import React, { useEffect, useState } from 'react'
import { Alert, Button, Card, Form, Input, InputNumber, Modal, Select, Switch, Table } from 'antd'
import { PlusOutlined } from '@ant-design/icons'
import PageTitle from 'components/PageTitle'
import {
  errorNotification,
  getRequest,
  inventoryPeopleUrl,
  inventoryPersonUrl,
  inventoryStatusUrl,
  patchRequest,
  postRequest,
  successNotification
} from '../../config'
import HealthStrip from './HealthStrip'
import { errorSentence, personPayload } from './utils'

// Who may command the bot. The Telegram USER id is the authority — never the chat — so a person has
// to message the bot once for their id to be found (see the note on the page). There is no delete:
// the audit log refers to these people, so you deactivate instead, and it takes effect on their next
// message.
const PeoplePage = () => {
  const [loading, setLoading] = useState(false)
  const [people, setPeople] = useState([])
  const [bots, setBots] = useState([])
  const [editing, setEditing] = useState(null) // null = closed, {} = new, person = edit
  const [saving, setSaving] = useState(false)
  const [form] = Form.useForm()

  const load = async () => {
    const response = await getRequest(inventoryPeopleUrl, 'people', setLoading)
    if (response.success) setPeople(response.data.results || response.data)
  }

  useEffect(() => {
    load()
    // The bots a person may be allowed on come from the status call (slug + name), which never
    // carries a token.
    getRequest(inventoryStatusUrl, 'bots', () => {}).then(response => {
      if (response.success) setBots(response.data.bots || [])
    })
    // eslint-disable-next-line
  }, [])

  const open = person => {
    setEditing(person)
    form.resetFields()
    if (person && person.id) {
      form.setFieldsValue({
        telegramUserId: person.telegramUserId,
        displayName: person.displayName,
        bots: person.bots,
        isActive: person.isActive
      })
    } else {
      form.setFieldsValue({ isActive: true, bots: [] })
    }
  }

  const save = async values => {
    const payload = personPayload(values)
    const isNew = !(editing && editing.id)
    const response = isNew
      ? await postRequest(inventoryPeopleUrl, payload, 'person', setSaving)
      : await patchRequest(inventoryPersonUrl(editing.id), payload, 'person', setSaving)
    if (response.success) {
      successNotification(isNew ? 'Person added' : 'Person updated')
      setEditing(null)
      load()
    } else {
      errorNotification(errorSentence(response, 'That could not be saved.'))
    }
  }

  const toggle = async person => {
    const response = await patchRequest(
      inventoryPersonUrl(person.id),
      { isActive: !person.isActive },
      'person',
      setLoading
    )
    if (response.success) load()
    else errorNotification(errorSentence(response, 'That could not be changed.'))
  }

  /* eslint-disable react/display-name */
  const columns = [
    { title: 'Name', dataIndex: 'displayName', key: 'displayName', render: v => v || '—' },
    { title: 'Telegram id', dataIndex: 'telegramUserId', key: 'telegramUserId' },
    { title: 'Acts as', dataIndex: 'userName', key: 'userName' },
    {
      title: 'Active',
      key: 'isActive',
      render: (_, r) => <Switch checked={r.isActive} onChange={() => toggle(r)} />
    },
    {
      title: '',
      key: 'edit',
      render: (_, r) => <Button onClick={() => open(r)}>Edit</Button>
    }
  ]
  /* eslint-enable react/display-name */

  return (
    <div className="container-fluid">
      <PageTitle title="Inventory assistant · People" />
      <HealthStrip />
      <Card
        title="Who may command the bot"
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={() => open({})}>
            Add a person
          </Button>
        }
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Authority is a person's Telegram user id — not the chat they are in."
          description="To find an id, the person messages @userinfobot on Telegram. Anyone not on this list is ignored without a reply, so the bot never confirms it is listening. People can be switched off but not removed, because the change log refers to them."
        />
        <Table
          rowKey="id"
          loading={loading}
          columns={columns}
          dataSource={people}
          pagination={false}
          scroll={{ x: 'max-content' }}
        />
      </Card>

      <Modal
        title={editing && editing.id ? 'Edit person' : 'Add a person'}
        open={!!editing}
        onCancel={() => setEditing(null)}
        okText="Save"
        confirmLoading={saving}
        onOk={() => form.submit()}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={save}>
          <Form.Item
            label="Telegram user id"
            name="telegramUserId"
            rules={[{ required: true, message: 'Required' }]}
          >
            <InputNumber style={{ width: '100%' }} disabled={!!(editing && editing.id)} />
          </Form.Item>
          <Form.Item label="Name" name="displayName">
            <Input />
          </Form.Item>
          <Form.Item
            label="Staff phone number or email"
            name="userIdentifier"
            extra={
              editing && editing.id
                ? `Currently acts as ${editing.userName}. Leave blank to keep it.`
                : 'The staff account this person acts as.'
            }
            rules={[{ required: !(editing && editing.id), message: 'Required' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item label="Bots" name="bots">
            <Select
              mode="multiple"
              options={bots.map(b => ({ value: b.id || b.slug, label: b.name }))}
              placeholder="Which bots they may use"
            />
          </Form.Item>
          <Form.Item label="Active" name="isActive" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}

export default PeoplePage
